import axios from 'axios';

// Auth and API base URLs
const AUTH_BASE_URL = (process.env.REACT_APP_AUTH_URL || 'https://tps-ai-skinanalysis.com/apiauth').trim();
const SURVEY_BASE_URL = (process.env.REACT_APP_SURVEY_URL || 'https://tps-ai-skinanalysis.com/surveyservice/api/v1').trim();
const RECOMMEND_BASE_URL = (process.env.REACT_APP_RECOMMEND_URL || 'https://tps-ai-skinanalysis.com/recommendservice/api/v1').trim();
const REPORT_BASE_URL = (process.env.REACT_APP_REPORT_URL || 'https://tps-ai-skinanalysis.com/report-service/api/v1').trim();
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').trim();
const UPLOAD_URL =
  (process.env.REACT_APP_UPLOAD_URL ||
    'https://tps-ai-skinanalysis.com/ai-communication/api/v1/ChunkedUpload/upload-chunk').trim();

// App-level auth device credentials (shared; not per visitor)
const AUTH_DEVICE_ID = '7d4f3036-e657-4d57-a5d6-d103340e72d3';
const DEVICE_SECRET = '1TDH6eN62AHy9NJWY4Hszdv2jj15zYpd1J5HxDkJk3U=';

/** Per-browser visitor id used as userId for survey / upload / report */
const VISITOR_ID_KEY = 'tps-visitor-id';

function createUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Create axios instance for auth
const authClient = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: false, // Set to false to avoid CORS issues with credentials
});

// Create axios instance for survey service
const surveyClient = axios.create({
  baseURL: SURVEY_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Create axios instance for recommendation service
const recommendClient = axios.create({
  baseURL: RECOMMEND_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Create axios instance for report service
const reportClient = axios.create({
  baseURL: REPORT_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Create axios instance for upload service (multipart)
const uploadClient = axios.create({
  timeout: 1200000, // chunk uploads can take longer
});

// Token management functions
export const tokenService = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  
  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// Set after first successful auth init (reset if visitor id is freshly created)
let tokenInitialized = false;

/** Returns a stable visitor UUID from localStorage (creates one on first visit). */
export function getVisitorId(): string {
  if (typeof window === 'undefined') {
    return AUTH_DEVICE_ID;
  }
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;
    const id = createUuid();
    window.localStorage.setItem(VISITOR_ID_KEY, id);
    // Drop tokens issued under the old shared userId so auth realigns to this visitor
    tokenService.clearTokens();
    tokenInitialized = false;
    return id;
  } catch {
    return createUuid();
  }
}

/** @deprecated Prefer getVisitorId() – kept for call sites that still import DEVICE_ID */
export const DEVICE_ID = AUTH_DEVICE_ID;

// Generate tokens using device credentials + per-visitor userId
export const generateTokens = async (
  userId: string = getVisitorId()
): Promise<{ accessToken: string; refreshToken: string }> => {
  try {
    const endpoint = '/api/v1/Token/generate-token';
    const fullUrl = `${AUTH_BASE_URL}${endpoint}`;
    console.log('Generating tokens - Full URL:', fullUrl);
    console.log('Base URL:', AUTH_BASE_URL);
    console.log('Endpoint:', endpoint);
    
    const { data } = await authClient.post(endpoint, {
      userId,
      deviceId: AUTH_DEVICE_ID,
      deviceSecret: DEVICE_SECRET,
    });
    
    // Store tokens
    if (data.accessToken && data.refreshToken) {
      tokenService.setTokens(data.accessToken, data.refreshToken);
    }
    
    return data;
  } catch (error: any) {
    console.error('Error generating tokens:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('Request made but no response received');
      console.error('This is likely a CORS issue. Please check:');
      console.error('1. Backend CORS configuration includes http://localhost:3001');
      console.error('2. Backend allows the required HTTP methods (POST, OPTIONS)');
      console.error('3. Backend allows Content-Type header');
    }
    throw error;
  }
};

// Initialize tokens on app start
export const initializeAuth = async (): Promise<void> => {
  // Ensure visitor UUID exists before issuing/using tokens
  getVisitorId();

  if (tokenInitialized) return;
  
  // Check if we already have tokens
  const existingToken = tokenService.getAccessToken();
  if (existingToken) {
    tokenInitialized = true;
    return;
  }
  
  // Generate new tokens
  try {
    await generateTokens();
    tokenInitialized = true;
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    throw error;
  }
};

// Request interceptor for survey client (for adding auth tokens)
surveyClient.interceptors.request.use(
  async (config) => {
    // Ensure we have tokens
    if (!tokenInitialized) {
      await initializeAuth();
    }
    
    // Add auth token if available
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for recommend client (for adding auth tokens)
recommendClient.interceptors.request.use(
  async (config) => {
    if (!tokenInitialized) {
      await initializeAuth();
    }
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor for report client (Bearer token)
reportClient.interceptors.request.use(
  async (config) => {
    if (!tokenInitialized) {
      await initializeAuth();
    }
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor (for adding auth tokens, etc.)
apiClient.interceptors.request.use(
  async (config) => {
    // Ensure we have tokens
    if (!tokenInitialized) {
      await initializeAuth();
    }
    
    // Add auth token if available
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for upload client (adds auth token)
uploadClient.interceptors.request.use(
  async (config) => {
    if (!tokenInitialized) {
      await initializeAuth();
    }
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh token function
export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken) {
    // No refresh token, generate new tokens
    const tokens = await generateTokens();
    return tokens.accessToken;
  }
  
  try {
    const { data } = await authClient.post('/api/v1/Token/refresh-token', {
      refreshToken,
      deviceId: AUTH_DEVICE_ID,
      deviceSecret: DEVICE_SECRET,
    });
    
    // Store new tokens
    if (data.accessToken && data.refreshToken) {
      tokenService.setTokens(data.accessToken, data.refreshToken);
      return data.accessToken;
    }
    
    // If response doesn't have tokens, fall back to generating new ones
    const tokens = await generateTokens();
    return tokens.accessToken;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // If refresh fails, try to generate new tokens
    try {
      const tokens = await generateTokens();
      return tokens.accessToken;
    } catch (genError) {
      console.error('Error generating new tokens after refresh failure:', genError);
      return null;
    }
  }
};

// Response interceptor for main API (handling errors globally and token refresh)
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration (401 Unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh token
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login or show error
        console.error('Token refresh failed:', refreshError);
        tokenService.clearTokens();
      }
    }
    
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for survey API (also handles token refresh)
surveyClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return surveyClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Survey token refresh failed:', refreshError);
        tokenService.clearTokens();
      }
    }

    if (error.response) {
      console.error('Survey API Error:', error.response.data);
    } else if (error.request) {
      console.error('Survey Network Error:', error.request);
    } else {
      console.error('Survey Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for recommend API (also handles token refresh)
recommendClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return recommendClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Recommend token refresh failed:', refreshError);
        tokenService.clearTokens();
      }
    }

    if (error.response) {
      console.error('Recommend API Error:', error.response.data);
    } else if (error.request) {
      console.error('Recommend Network Error:', error.request);
    } else {
      console.error('Recommend Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for upload API (handles token refresh)
uploadClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return uploadClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Upload token refresh failed:', refreshError);
        tokenService.clearTokens();
      }
    }

    return Promise.reject(error);
  }
);

// Survey API endpoints
export const surveyApi = {
  // Get all surveys
  getSurveys: async () => {
    const response = await surveyClient.get('/Surveys');
    return response.data;
  },

  // Get survey details including questions
  getSurveyDetails: async (surveyId: string) => {
    const response = await surveyClient.get(`/Surveys/${surveyId}`);
    return response.data;
  },

  // Create a new survey response and return its id
  // This should be called before starting the questionnaire
  createSurveyResponse: async (surveyId: string, userId: string = getVisitorId()) => {
    const response = await surveyClient.post(`/Surveys/${surveyId}/responses`, {
      surveyId,
      userId,
    });
    return response.data;
  },

  // Submit all answers for a given responseId (with optional client and location)
  submitSurveyAnswers: async (
    responseId: string,
    payload: {
      client: {
        platform: string;
        appVersion: string;
        timezone: string;
        locale: string;
      };
      location: {
        latitude: number;
        longitude: number;
        accuracyMeters: number;
        provider: string;
        capturedAt: string;
      } | null;
      answers: {
        questionId: string;
        optionId: string | null;
        answerText: string | null;
        answerNumber: number | null;
        answerDate: string | null;
        answerRating: number | null;
      }[];
    }
  ) => {
    const response = await surveyClient.post(`/responses/${responseId}/answers`, payload);
    return response.data;
  },

  // Compute product recommendations based on analysis scores and survey answers
  computeRecommendations: async (payload: {
    analysisScores: {
      Acne: number;
      Redness: number;
      Spots: number;
      Dryness: number;
      Wrinkles: number;
      DarkCircles: number;
    };
    surveyAnswers: {
      questionId: string;
      optionIds: string[];
      answerNumber: number | null;
    }[];
    topN?: number;
    language?: 'EN' | 'DE' | 'TR' | 'ES' | 'FR';
  }) => {
    const response = await recommendClient.post('/Recommendations/compute', payload);
    return response.data;
  },
};

// Report service API (Reports/compute) – pdf and emailTo are in the JSON payload
export interface ReportComputePayload {
  userId: string;
  profileId: string;
  pdf: boolean;
  emailTo?: string;
  analysisScores: {
    Acne: number;
    Redness: number;
    Spots: number;
    Dryness: number;
    Wrinkles: number;
    DarkCircles: number;
  };
  overlays: {
    Acne?: string;
    Redness?: string;
    Spots?: string;
    Dryness?: string;
    Wrinkles?: string;
    DarkCircles?: string;
  };
  image: string;
  products: ReportComputeProduct[];
  includeComparison: boolean;
  explain: boolean;
}
export interface ReportComputeProduct {
  productId?: string;
  title?: string;
  shopifyProductId?: string;
  score?: number;
  rutinOnerisi?: string;
  images?: { url?: string; altText?: string }[];
  variants?: { id?: string; title?: string; priceAmount?: number; currencyCode?: string }[];
}

export const reportApi = {
  computeReport: async (payload: ReportComputePayload) => {
    const response = await reportClient.post('/Reports/compute', payload);
    return response.data;
  },
  sendReportEmail: async (payload: ReportComputePayload & { emailTo: string }) => {
    const response = await reportClient.post('/Reports/compute', {
      ...payload,
      pdf: true,
    });
    return response.data;
  },
};

// Upload API endpoints
export const uploadApi = {
  uploadSelfieChunked: async (params: {
    dataUrl: string; // e.g. canvas.toDataURL('image/jpeg', 0.9)
    userId?: string;
    fileName?: string;
    chunkSizeBytes?: number; // default 10KB
    concurrentRequests?: number; // default 1 (sequential)
    onProgress?: (uploadedChunks: number, totalChunks: number) => void;
  }) => {
    const {
      dataUrl,
      userId = getVisitorId(),
      fileName = 'selfie.jpg',
      chunkSizeBytes = 10 * 1024,
      concurrentRequests = 1,
      onProgress,
    } = params;

    if (!dataUrl) {
      throw new Error('No image dataUrl provided for upload.');
    }

    // Convert dataUrl -> Blob
    const blob = await (await fetch(dataUrl)).blob();
    const totalChunks = Math.max(1, Math.ceil(blob.size / chunkSizeBytes));

    const uploadId =
      (globalThis.crypto && 'randomUUID' in globalThis.crypto
        ? (globalThis.crypto as Crypto).randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`) || `${Date.now()}`;

    const fileExtension = fileName.includes('.') ? fileName.split('.').pop() || 'jpg' : 'jpg';

    const uploadChunk = async (chunkIndex: number) => {
      const start = chunkIndex * chunkSizeBytes;
      const end = Math.min(start + chunkSizeBytes, blob.size);
      const chunkBlob = blob.slice(start, end, blob.type || 'image/jpeg');

      // Skip empty chunks defensively
      if (chunkBlob.size === 0) {
        return null;
      }

      const formData = new FormData();
      formData.append('Chunk', chunkBlob, fileName);
      formData.append('UploadId', uploadId);
      formData.append('ChunkIndex', String(chunkIndex));
      formData.append('TotalChunks', String(totalChunks));
      formData.append('FileExtension', fileExtension);
      formData.append('FileName', fileName);
      formData.append('UserId', userId);

      // Let the browser set multipart form-data headers (boundary etc.)
      const resp = await uploadClient.post(UPLOAD_URL, formData);
      return resp.data;
    };

    // Sequential by default (as requested)
    if (concurrentRequests <= 1) {
      let lastResponse: any = null;
      for (let i = 0; i < totalChunks; i++) {
        lastResponse = await uploadChunk(i);
        if (onProgress) {
          onProgress(i + 1, totalChunks);
        }
      }
      return { uploadId, totalChunks, lastResponse };
    }

    // Simple concurrency support (if you increase it later)
    const results: any[] = new Array(totalChunks);
    let nextIndex = 0;
    let uploaded = 0;

    const worker = async () => {
      while (nextIndex < totalChunks) {
        const current = nextIndex;
        nextIndex += 1;
        results[current] = await uploadChunk(current);
        uploaded += 1;
        if (onProgress) {
          onProgress(uploaded, totalChunks);
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrentRequests, totalChunks) }, () => worker()));
    return { uploadId, totalChunks, results };
  },
};

// API endpoints
export const api = {
  // Submit questionnaire answers
  submitQuestionnaire: async (answers: {
    ageRange: string;
    skinType: string;
    skinConcern: string;
    skinRoutine: string;
  }) => {
    const response = await apiClient.post('/questionnaire', answers);
    return response.data;
  },

  // Submit face analysis (image + questionnaire answers)
  submitFaceAnalysis: async (data: {
    image: string; // Base64 encoded image
    questionnaireAnswers: {
      ageRange: string;
      skinType: string;
      skinConcern: string;
      skinRoutine: string;
    };
  }) => {
    const response = await apiClient.post('/analysis', data);
    return response.data;
  },

  // Get analysis results
  getAnalysisResults: async (analysisId: string) => {
    const response = await apiClient.get(`/analysis/${analysisId}`);
    return response.data;
  },

  // Get products list
  getProducts: async () => {
    const response = await apiClient.get('/products');
    return response.data;
  },
};

export default apiClient;
